/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, {useEffect, useState} from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { FormattedMessage } from 'react-intl';
import JSG from '@cedalo/jsg-ui';
import HelpIcon from '@mui/icons-material/esm/HelpOutline';
import { graphManager } from '../../../GraphManager';
import { functionStrings } from '../../../languages/FunctionStrings';
import SettingsPaper from '../../utils/SettingsPaper';
import {Box, FormGroup, ListItemButton, Paper} from '@mui/material';
import Typography from '@mui/material/Typography';
import ReactMarkdown from 'react-markdown';
import parse from 'html-react-parser';
import remarkGfm from 'remark-gfm'

const { Expression } = require('@cedalo/jsg-core');
const { SheetCommandFactory } = require('@cedalo/jsg-core');

const getFunctions = (filt, cat) => {
	let uniqueId = 0;
	const data = [];

	functionStrings.enumerateFunctions(cat, (name, categ, parameters, description, exp, info) => {
		const experimental = localStorage.getItem('experimental') === 'true';
		if (filt.length) {
			if (!name.toLowerCase().includes(filt.toLowerCase())) {
				return;
			}
		}

		if (info.cellEditor === false || (exp === true && !experimental)) {
			return;
		}

		let argumentList = '';
		const params = parameters.split(',');
		const { parameter } = JSG.getParserLocaleSettings().separators;

		if (params.length && params[0].length) {
			params.forEach((param, index) => {
				argumentList += params.length === index + 1 ? `${param}` : `${param}${parameter}`;
			});
		}

		data.push({
			name,
			categ,
			argumentList,
			description,
			selected: false,
			id: uniqueId,
			info
		});
		uniqueId += 1;
	});

	return data;
};

const getCategories = () => {
	const categories = [];
	functionStrings.enumerateCategories((id, name) => {
		categories.push(
			<MenuItem value={id} key={id}>
				{name}
			</MenuItem>
		);
	});
	return categories;
};

export function InsertFunctionSettings(props) {
	const [category, setCategory] = useState('All');
	const [filter, setFilter] = useState('');
	const [functions, setFunctions] = useState([]);
	const [funcInfo, setFuncInfo] = useState();
	const [funcName, setFuncName] = useState();
	const [funcIndex, setFuncIndex] = useState(0);
	const [selected, setSelected] = useState('');


	useEffect(() => {
		setFunctions(getFunctions(filter, category));
	}, [props.open]);

	const handleCategoryChange = (event) => {
		setCategory(event.target.value);
		setFuncInfo();
		setFunctions(getFunctions(filter, event.target.value));
	};

	const handleFunctionClick = (id, name, info) => {
		setFuncInfo(info);
		setFuncName(name);
		setFuncIndex(id);
		setSelected(id);
	};

	const handleExampleMouseDown = (event) => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView === undefined) {
			return;
		}
		const item = sheetView.getItem();
		const pos = item.getOwnSelection().getActiveCell();
		if (pos === undefined) {
			return;
		}

		const formula = event.target.textContent.substring(1);
		const expr = new Expression(0, formula);

		expr.evaluateForSheet(item.model);
		expr.correctFormula(item.model);
		const ref = item.getOwnSelection().activeCellToString();
		const cmd = SheetCommandFactory.create('command.SetCellDataCommand', item.model, ref, expr, false);
		graphManager.synchronizedExecute(cmd);
		sheetView.notify();

	}

	const handleFunctionDoubleClick = (key) => {
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView === undefined || key === -1) {
			return;
		}
		const item = sheetView.getItem();
		const pos = item.getOwnSelection().getActiveCell();
		if (pos === undefined) {
			return;
		}

		// put all params in "" to avoid parameters to be identified as known attribute name
		let paramsToInsert = '';
		const { parameter } = JSG.getParserLocaleSettings().separators;
		const params = functions[key].argumentList.split(parameter);

		if (params.length && params[0].length) {
			params.forEach((param, index) => {
				paramsToInsert += params.length === index + 1 ? `"${param}"` : `"${param}",`;
			});
		}

		const formula = `${functions[key].name}(${paramsToInsert})`;
		const div = JSG.editDiv;
		if (div && (div.contentEditable === 'true' || div.tagName === 'INPUT')) {
			div.append(document.createTextNode(formula));
		} else {
			const expr = new Expression(0, formula);
			expr.evaluateForSheet(item.model);
			expr.correctFormula(item.model);
			const ref = item.getOwnSelection().activeCellToString();
			const cmd = SheetCommandFactory.create('command.SetCellDataCommand', item.model, ref, expr, false);
			graphManager.synchronizedExecute(cmd);
			sheetView.notify();
		}
	};

	const handleHelp = (key) => {
		const VERSION = process.env.REACT_APP_VERSION || '2.5.0';
		const semverRegExp = /(?<major>0|[1-9]\d*)\.(?<minor>0|[1-9]\d*)\.*(?<patch>0|[1-9]\d*)*/i;
		const versionObject = VERSION.match(semverRegExp);
		const versionLink = `${versionObject.groups.major}.${versionObject.groups.minor}`;

		const url = `https://docs.cedalo.com/streamsheets/${versionLink}/functions/${functions[key].categ}/${functions[
			key
		].name.replace(/\./g, '')}.html`;
		window.open(url.toLowerCase(), '_blank');
	};

	if (!props.open) {
		return null;
	}

	return (
		<SettingsPaper
			open={props.open}
			close={props.close}
			helpContext="insertfunctionsettings"
			title={<FormattedMessage id="InsertFunctionSettings.Title" defaultMessage="Insert Function" />}
		>
			<FormGroup>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					select
					value={category}
					onChange={handleCategoryChange}
					label={<FormattedMessage id="InsertFunctionSettings.Category" defaultMessage="Category" />}
				>
					{getCategories()}
				</TextField>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					type="search"
					label={<FormattedMessage id="InsertFunctionSettings.Filter" defaultMessage="Filter" />}
					autoFocus
					defaultValue={filter}
					onChange={(event) => {
						setFuncInfo();
						setFilter(event.target.value);
						setFunctions(getFunctions(event.target.value, category));
					}}
				/>
				<Typography color="textPrimary" sx={{ mt: '10px' }}>
					<FormattedMessage id="InsertFunctionSettings.ListInfo" defaultMessage="Click to show function info and double click to paste to selected cell" />
				</Typography>
				<Paper
					variant="outlined"
					style={{
						marginTop: '10px',
						height: '200px',
						overflowY: 'scroll'
					}}
				>
					<List dense disablePadding>
						{functions.map((row) => (
							<ListItemButton
								key={row.id}
								selected={selected === row.id}
								button
								onClick={() => handleFunctionClick(row.id, row.name, row.info)}
								onDoubleClick={() => handleFunctionDoubleClick(row.id)}
							>
								<ListItemText
									style={{
										wordBreak: 'break-all'
									}}
									primary={`${row.name}`}
								/>
							</ListItemButton>
						))}
					</List>
				</Paper>
				{funcInfo
					? [
							<Paper
								variant="outlined"
								style={{
									marginTop: '20px',
									padding: '10px'
								}}
							>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
										{funcName}
									</Typography>
									<IconButton onClick={() => handleHelp(funcIndex)} size="large">
										<HelpIcon />
									</IconButton>
								</div>
								<div style={{ fontSize: '8pt', width: '255px', lineHeight: '1.3' }}>
									<p style={{}}>{functionStrings.getDescriptor(funcInfo, 'en', 'description')}</p>
									<p
										style={{
											fontWeight: 'bold',
											fontSize: '9pt',
											marginTop: '10px',
											marginBottom: '5px'
										}}
									>
										<FormattedMessage id="InsertFunctionSettings.Parameters" defaultMessage="Parameters" />
									</p>
									{funcInfo.default.arguments &&
										funcInfo.default.arguments.map((argument) => [
											<p style={{ fontWeight: 'bold', marginLeft: '5px' }}>{argument.name}</p>,
											<p style={{ marginLeft: '10px' }}>{parse(argument.description)}</p>
										])}
									<p style={{ fontWeight: 'bold', fontSize: '9pt', marginTop: '10px' }}>
										<FormattedMessage id="InsertFunctionSettings.Examples" defaultMessage="Examples (click to paste into cell)" />
									</p>
									{funcInfo.default.examples && funcInfo.default.examples.infoStart ?
										<Box
											id="funcexampletable"
											sx={{
												margin: '6px',
												overflowX: 'auto'
											}}
										>
											{/*<FormattedMessage id="InsertFunctionSettings.SourceData" defaultMessage="Source Data" />*/}
											<ReactMarkdown remarkPlugins={[remarkGfm]}>{funcInfo.default.examples.infoStart}</ReactMarkdown>
										</Box>
										: null}
									{funcInfo.default.examples && funcInfo.default.examples.formulas &&
										funcInfo.default.examples.formulas.map((example) => (
											<Box
												onMouseDown={handleExampleMouseDown}
												sx={{
													cursor: 'pointer',
													marginLeft: '5px',
													'&:hover': {
														backgroundColor: `rgba(0, 0, 0, 0.04)`
													}
												}}
											>
												<div
													style={{
														fontFamily: 'monospace',
														width: '250px',
														wordBreak: 'break-word',
														fontSize: '11px',
														margin: '3px 0px',
														paddingLeft: '6px',
														textIndent: '-6px'
												}}
												>
													{parse(example.formula)}
												</div>
											</Box>
										))}
								</div>
							</Paper>
					  ]
					: null}
			</FormGroup>
		</SettingsPaper>
	);
}
